import React, { ForwardedRef, useState } from 'react';
import styled, { CSSObject } from 'styled-components';
import { globalTheme } from 'styles/global-theme';
import { getStateValues } from '../input';
import { AnimatePresence } from 'framer-motion';
import { InputMessage } from '../input-message';

const getBorderColor = (props: {
  color: keyof typeof globalTheme.colors;
}): CSSObject => ({
  borderColor: globalTheme.colors[props.color],
});

const StyledTextArea = styled.textarea`
  resize: none;
  font-size: ${globalTheme.fontSizes.default};
  padding: ${globalTheme.space[0]} ${globalTheme.space[1]};
  background-color: ${globalTheme.colors.white};
  border-radius: 8px;
  transition: border-color ${globalTheme.transitions.fast};
  width: 100%;
  border: none;
  outline: none;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.8;
  }
`;

export const TextAreaWrapper = styled.div`
  display: flex;
  align-items: center;
  background-color: ${globalTheme.colors.white};
  border: 2px solid ${globalTheme.colors.neutral};
  border-radius: 8px;
  transition: border-color ${globalTheme.transitions.fast};
  width: 100%;

  /* Border color override */
  ${getBorderColor}

  &:focus-within {
    border-color: ${globalTheme.colors.darkGrey};
  }
`;

interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  state?: 'error' | 'success' | 'default';
  message?: string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { state = 'default', value, onChange, message, ...props },
    ref: ForwardedRef<HTMLTextAreaElement>
  ) => {
    const { stateColor, stateTextColor } = getStateValues(state);

    const [inputValue, setInputValue] = useState(value);

    // Popper reference element
    const [referenceElement, setReferenceElement] =
      useState<HTMLDivElement | null>(null);
    return (
      <TextAreaWrapper color={stateColor} ref={setReferenceElement}>
        <StyledTextArea
          ref={ref}
          rows={props.rows || 3}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            onChange?.(e);
          }}
          {...props}
        />
        <AnimatePresence>
          {message && (
            <InputMessage
              backgroundColor={stateColor}
              referenceElement={referenceElement}
              color={stateTextColor}
            >
              {message}
            </InputMessage>
          )}
        </AnimatePresence>
      </TextAreaWrapper>
    );
  }
);
